import {InputDate} from '@/components/commons/input-date/InputDate'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {useGetHolidays} from '@/features/projects/queries/useGetHolidays'
import {getNextWorkingDay} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'
import dayjs from 'dayjs'
import {useController, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const ProjectFormEndDateFieldSchema = ProjectFieldsSchema.pick({
    startDate: true,
    endDate: true,
    expirationDate: true,
    durationDays: true,
    referringProject: true
})
type ProjectFormEndDateFieldSchema = z.infer<typeof ProjectFormEndDateFieldSchema>

export const ProjectFormEndDateField = () => {
    const {t} = useTranslation()
    const {control} = useFormContext<ProjectFormEndDateFieldSchema>()
    const {field, fieldState} = useController({control, name: 'endDate'})
    const {field: startDateField} = useController({control, name: 'startDate'})
    const {field: expirationDateField} = useController({control, name: 'expirationDate'})
    const durationDays = useWatch({control, name: 'durationDays'})
    const referringProject = useWatch({control, name: 'referringProject'})
    const {data: holidays} = useGetHolidays({
        urlParams: {year: dayjs(startDateField.value).year()},
        options: {
            enabled: dayjs(startDateField.value).isValid()
        }
    })

    const handleChangeEndDateField = (date: Date | null) => {
        field.onChange(date)
        const newStartDate = date ? getNextWorkingDay(date, holidays || []) : null
        startDateField.onChange(newStartDate)
        expirationDateField.onChange(
            newStartDate
                ? dayjs(newStartDate)
                      .add(durationDays || 0, 'day')
                      .toDate()
                : null
        )
    }

    return (
        <InputDate
            label={t('projects:end_date')}
            value={field.value}
            onChange={handleChangeEndDateField}
            placeholder="GG/MM/AAAA"
            defaultMonth={
                referringProject?.startDate ? getNextWorkingDay(new Date(referringProject.startDate)) : undefined
            }
            disableBeforeDate={referringProject?.startDate ? dayjs(referringProject.startDate).toDate() : undefined}
            errorMessage={fieldState.error?.message}
            fullWidth
        />
    )
}
